<script setup lang="ts">
import type { CmsElementForm } from '@shopware/composables'
import { ApiClientError } from "@shopware/api-client"

const props = defineProps<{
    content?: CmsElementForm
}>()

// Composables
const { t } = useI18n()
const { apiClient } = useShopwareContext()
const { foreignKey } = useNavigationContext()
const { resolveApiErrors } = useApiErrorsResolver()
const { recaptcha } = useRecaptchaV3()

// State
const isLoading = ref(false)
const formSent = ref(false)
const formError = ref<string | null>(null)
const formData = reactive({
    salutationId: '',
    firstName: '',
    lastName: '',
    email: '',
    contractNumber: '',
    comment: '',
})

// Computed
const title = computed(() => {
    return props.content?.config?.title?.value ?? t('revocation.headline')
})

const confirmationText = computed(() => {
    return props.content?.config?.confirmationText?.value ?? t('revocation.successMessage')
})

const salutationOptions = computed(() =>
    (props.content?.data ?? []).map(salutation => ({
        value: salutation.id,
        label: salutation.translated?.displayName ?? salutation.displayName
    }))
)

const isFormValid = computed(() => {
    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    const isContractNumberFilled = formData.contractNumber.trim().length > 0

    return isEmailValid && isContractNumberFilled && !isLoading.value
})

const handleSubmit = async () => {
    if (!isFormValid.value) return
    isLoading.value = true
    formError.value = null

    try {
        const isNotABot = await recaptcha()

        if (!isNotABot) {
            formError.value = t('revocation.errorMessage')
            return
        }

        await apiClient.invoke("sendRevocationRequestForm post /revocation-request-form", {
            body: {
                ...formData,
                navigationId: foreignKey.value,
            },
        })
        formSent.value = true
    } catch (apiError) {
        if (apiError instanceof ApiClientError) {
            const errorMessages = resolveApiErrors(apiError.details.errors)
            formError.value = errorMessages.join(', ') || t('revocation.errorMessage')
        } else {
            formError.value = apiError instanceof Error ? apiError.message : t('revocation.errorMessage')
        }
    } finally {
        isLoading.value = false
    }
}
</script>

<template>
    <div>
        <FoundationHeadline tag="h3" class="px-4 -ml-4 text-lg font-medium mb-2">
            {{ title }}
        </FoundationHeadline>

        <div v-if="formSent" class="flex items-start gap-3 rounded-lg bg-success/10 border border-success p-4 text-success" role="alert">
            <FoundationIcon name="circle-check" class="mt-0.5 shrink-0" />
            <p>{{ confirmationText }}</p>
        </div>

        <form v-else @submit.prevent="handleSubmit">
            <div class="space-y-6">
                <div class="flex flex-col gap-4">
                    <!-- Salutation, First Name, Last Name Row -->
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <!-- Salutation -->
                        <div class="w-full">
                            <FoundationLabel for="revocation-salutation" class="block sr-only">
                                {{ $t('form.salutation') }}
                            </FoundationLabel>
                            <FoundationSelect
                                id="revocation-salutation"
                                v-model="formData.salutationId"
                                :placeholder="$t('form.salutation')"
                                :options="salutationOptions"
                                :disabled="isLoading"
                                name="salutation"
                                class="w-full"
                            />
                        </div>

                        <!-- First Name -->
                        <ComponentTextInput
                            id="revocation-first-name"
                            v-model="formData.firstName"
                            :label="$t('form.firstName')"
                            type="text"
                            :placeholder="$t('form.firstName')"
                            :disabled="isLoading"
                            size="md"
                            bordered
                        />

                        <!-- Last Name -->
                        <ComponentTextInput
                            id="revocation-last-name"
                            v-model="formData.lastName"
                            :label="$t('form.lastName')"
                            type="text"
                            :placeholder="$t('form.lastName')"
                            :disabled="isLoading"
                            size="md"
                            bordered
                        />
                    </div>

                    <!-- Email and Contract Number Row -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <!-- Email -->
                        <ComponentTextInput
                            id="revocation-email"
                            v-model="formData.email"
                            :label="$t('form.email')+$t('form.required')"
                            type="email"
                            :placeholder="$t('form.email')+$t('form.required')"
                            required
                            :disabled="isLoading"
                            size="md"
                            bordered
                        />

                        <!-- Contract / Order Number -->
                        <ComponentTextInput
                            id="revocation-contract-number"
                            v-model="formData.contractNumber"
                            :label="$t('revocation.contractNumber')+$t('form.required')"
                            type="text"
                            :placeholder="$t('revocation.contractNumberPlaceholder')"
                            required
                            :disabled="isLoading"
                            size="md"
                            bordered
                        />
                    </div>

                    <!-- Comment (optional) -->
                    <ComponentTextarea
                        id="revocation-comment"
                        v-model="formData.comment"
                        :label="$t('revocation.comment')"
                        :placeholder="$t('revocation.commentPlaceholder')"
                        :rows="5"
                        :disabled="isLoading"
                        bordered
                    />

                    <!-- Privacy Notice -->
                    <p class="text-sm">
                        {{ $t('revocation.privacyNotice') }}
                        <FoundationLink is="internal" to="/datenschutz">
                            {{ $t('revocation.privacyNoticeLinkText') }}
                        </FoundationLink>
                        {{ $t('revocation.privacyNoticeEnd') }}
                    </p>

                    <!-- reCAPTCHA Notice -->
                    <p class="text-sm">
                        {{ $t('form.recaptchaHint') }}
                        <FoundationLink is="external" to="https://policies.google.com/privacy" target="_blank">
                            {{ $t('form.recaptchaPrivatePolicy') }}
                        </FoundationLink>
                        {{ $t('form.recaptchaAnd') }}
                        <FoundationLink is="external" to="https://policies.google.com/terms" target="_blank">
                            {{ $t('form.recaptchaTac') }}
                        </FoundationLink>
                        {{ $t('form.recaptchaOf') }}
                    </p>

                    <!-- Error Message -->
                    <div v-if="formError" class="flex items-start gap-3 rounded-lg bg-error/10 border border-error p-4 text-error" role="alert">
                        <FoundationIcon name="circle-x" class="mt-0.5 shrink-0" />
                        <p>{{ formError }}</p>
                    </div>

                    <!-- Submit Button -->
                    <div class="flex flex-col sm:flex-row gap-3 pt-4">
                        <FoundationButton
                            type="submit"
                            color="secondary"
                            class="flex-1"
                            :loading="isLoading"
                            :disabled="!isFormValid"
                            @click="handleSubmit"
                        >
                            {{ $t('revocation.submit') }}
                        </FoundationButton>
                    </div>

                    <div class="text-sm">{{ $t('form.requiredFieldsInfo') }}</div>
                </div>
            </div>
        </form>
    </div>
</template>
